import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { AdvancePicker } from "@/components/AdvancePicker";
import api from "@/lib/api";

export const EXP_CATS = ["electricite", "eau", "ascenseur", "gardiennage", "nettoyage", "jardinage", "reparations", "assurance", "syndic", "autres"];
export const DOC_CATS = ["reglement", "contrat", "legal", "pv", "attestation", "facture", "autre"];

export function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-sm font-semibold text-muted-foreground">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-12 rounded-xl border border-input bg-background px-3 mt-1 outline-none" />
    </div>
  );
}

export function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-sm font-semibold text-muted-foreground">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-12 rounded-xl border border-input bg-background px-3 mt-1 outline-none">
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}

// Formulaire de révision d'une FACTURE (montants, statut de paiement, avance).
export function ExpenseFields({ t, exp, setExp, isTres }) {
  const [sugg, setSugg] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!exp.category) { setSugg(null); return; }
    api.get("/expenses/label-suggestion", { params: { category: exp.category, date: exp.date || "" } })
      .then((r) => { if (alive) setSugg(r.data?.suggestion || null); })
      .catch(() => { if (alive) setSugg(null); });
    return () => { alive = false; };
  }, [exp.category, exp.date]);
  return (
    <div className="space-y-3" data-testid="smart-expense-fields">
      <F label={t("supplier")} value={exp.supplier} onChange={(v) => setExp({ ...exp, supplier: v })} testid="smart-exp-supplier" />
      {sugg && sugg !== (exp.supplier || "") && (
        <button type="button" onClick={() => setExp({ ...exp, supplier: sugg })} data-testid="smart-exp-label-suggestion"
          className="-mt-1 w-full flex items-center gap-2 rounded-xl bg-majorelle/10 text-majorelle px-3 py-2 text-xs font-semibold text-start">
          <Icons.Sparkles className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">{t("recurring_label_suggest")} : « {sugg} »</span>
          <span className="underline shrink-0">{t("recurring_label_use")}</span>
        </button>
      )}
      <div className="grid grid-cols-2 gap-3">
        <F label={t("amount")} type="number" value={exp.amount} onChange={(v) => setExp({ ...exp, amount: v })} testid="smart-exp-amount" />
        <F label={t("vat")} type="number" value={exp.vat_amount} onChange={(v) => setExp({ ...exp, vat_amount: v })} testid="smart-exp-vat" />
      </div>
      <F label={t("date")} type="date" value={exp.date} onChange={(v) => setExp({ ...exp, date: v })} testid="smart-exp-date" />
      <Sel label={t("category")} value={exp.category} onChange={(v) => setExp({ ...exp, category: v })}
        options={EXP_CATS.map((c) => [c, t("cat_" + c)])} testid="smart-exp-category" />
      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("payment_status_label")}</label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {[["en_attente", t("pay_pending")], ["paye", t("pay_paid")], ["avance", t("pay_advanced")]].map(([val, lbl]) => {
            const sel = exp.payment_method === "avance" ? "avance" : exp.payment_status;
            return (
              <button key={val} data-testid={`smart-pstatus-${val}`}
                onClick={() => setExp({
                  ...exp,
                  payment_status: val === "en_attente" ? "en_attente" : "paye",
                  payment_method: val === "avance" ? "avance" : (val === "paye" && exp.payment_method !== "avance" ? exp.payment_method : null),
                  advanced_by_id: val === "avance" ? exp.advanced_by_id : null,
                  _advConfirmed: false,
                })}
                className={`h-11 rounded-xl border font-semibold text-xs px-1 ${sel === val ? "bg-brand text-white border-brand" : "bg-background border-input"}`}>
                {lbl}
              </button>
            );
          })}
        </div>
      </div>
      {exp.payment_status === "paye" && exp.payment_method !== "avance" && (
        <div className="grid grid-cols-2 gap-2">
          {[["bank", t("pm_bank"), true], ["cash", t("pm_cash"), isTres]].map(([val, lbl, ok]) => (
            <button key={val} disabled={!ok} onClick={() => setExp({ ...exp, payment_method: val })} data-testid={`smart-pmethod-${val}`}
              className={`h-11 rounded-xl border font-semibold text-sm ${exp.payment_method === val ? "bg-majorelle text-white border-majorelle" : "bg-background border-input"} ${!ok ? "opacity-40" : ""}`}>
              {lbl}
            </button>
          ))}
        </div>
      )}
      {exp.payment_method === "avance" && (
        <AdvancePicker t={t} value={exp.advanced_by_id} confirmed={!!exp._advConfirmed}
          onPick={(id) => setExp({ ...exp, advanced_by_id: id, _advConfirmed: false })}
          onConfirm={(v) => setExp({ ...exp, _advConfirmed: v })} testidPrefix="smart" />
      )}
    </div>
  );
}

// Formulaire de révision d'un RELEVÉ BANCAIRE (date, solde, aperçu des opérations).
export function BankFields({ t, bank, setBank }) {
  return (
    <div className="space-y-3" data-testid="smart-bank-fields">
      <div className="grid grid-cols-2 gap-3">
        <F label={t("ss_statement_date")} type="date" value={bank.statement_date} onChange={(v) => setBank({ ...bank, statement_date: v })} testid="smart-bank-date" />
        <F label={t("ss_closing_balance")} type="number" value={bank.closing_balance} onChange={(v) => setBank({ ...bank, closing_balance: v })} testid="smart-bank-balance" />
      </div>
      <div className="rounded-2xl border border-input p-3">
        <p className="text-sm font-bold mb-2" data-testid="smart-tx-count">
          {(bank.transactions || []).length} {t("ss_tx_count")}
        </p>
        {(bank.transactions || []).slice(0, 5).map((tx, i) => (
          <div key={`${tx.date}|${tx.label}|${tx.debit}|${tx.credit}|${i}`} className="flex items-center justify-between text-xs py-1 border-t border-border/50 first:border-0">
            <span className="truncate flex-1">{tx.date} · {tx.label}</span>
            <span className={`font-mono font-bold ms-2 ${Number(tx.debit) > 0 ? "text-terracotta" : "text-brand dark:text-primary"}`}>
              {Number(tx.debit) > 0 ? `-${tx.debit}` : `+${tx.credit}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Formulaire de révision d'un RIB (résidence ou externe).
export function RibFields({ t, rib, setRib }) {
  return (
    <div className="space-y-3" data-testid="smart-rib-fields">
      <div className="grid grid-cols-2 gap-2">
        {[["residence", t("rib_kind_residence")], ["externe", t("rib_kind_externe")]].map(([val, lbl]) => (
          <button key={val} onClick={() => setRib({ ...rib, kind: val })} data-testid={`smart-rib-kind-${val}`}
            className={`h-12 rounded-xl border font-semibold text-xs px-1 ${rib.kind === val ? "bg-brand text-white border-brand" : "bg-background border-input"}`}>
            {lbl}
          </button>
        ))}
      </div>
      {rib.kind === "externe" && (
        <F label={t("rib_label")} value={rib.label} onChange={(v) => setRib({ ...rib, label: v })} testid="smart-rib-label" />
      )}
      <F label={t("rib_holder")} value={rib.holder} onChange={(v) => setRib({ ...rib, holder: v })} testid="smart-rib-holder" />
      <div className="grid grid-cols-2 gap-3">
        <F label={t("rib_bank")} value={rib.bank_name} onChange={(v) => setRib({ ...rib, bank_name: v })} testid="smart-rib-bank" />
        <F label={t("rib_agency")} value={rib.agency} onChange={(v) => setRib({ ...rib, agency: v })} testid="smart-rib-agency" />
      </div>
      <F label={t("rib_number")} value={rib.rib_number} onChange={(v) => setRib({ ...rib, rib_number: v })} testid="smart-rib-number" />
      <F label={t("rib_iban")} value={rib.iban} onChange={(v) => setRib({ ...rib, iban: v })} testid="smart-rib-iban" />
    </div>
  );
}

// Formulaire de révision d'un DOCUMENT (titre, catégorie, visibilité).
export function DocFields({ t, docF, setDocF }) {
  return (
    <div className="space-y-3" data-testid="smart-doc-fields">
      <F label={t("doc_title")} value={docF.title} onChange={(v) => setDocF({ ...docF, title: v })} testid="smart-doc-title" />
      <Sel label={t("doc_category")} value={docF.category} onChange={(v) => setDocF({ ...docF, category: v })}
        options={DOC_CATS.map((c) => [c, t("doc_" + c)])} testid="smart-doc-category" />
      <F label={t("doc_description")} value={docF.description} onChange={(v) => setDocF({ ...docF, description: v })} testid="smart-doc-description" />
      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("visibility")}</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {[["public", t("vis_public")], ["prive", t("vis_prive")]].map(([val, lbl]) => (
            <button key={val} onClick={() => setDocF({ ...docF, visibility: val })} data-testid={`smart-doc-vis-${val}`}
              className={`h-11 rounded-xl border font-semibold text-xs px-1 ${docF.visibility === val ? "bg-brand text-white border-brand" : "bg-background border-input"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
